import React, { useState, useEffect, useCallback } from "react";
import "../styles/toast.scss";

/**
 * Toast — Notification toast component
 *
 * @param {string} message — text to display
 * @param {string} type — "success" | "error" | "info" (default: "success")
 * @param {number} duration — auto-dismiss time in ms (default: 4000, 0 = no auto-dismiss)
 * @param {boolean} isVisible — show/hide
 * @param {function} onClose — called when toast dismisses
 */
const Toast = ({ message, type = "success", duration = 4000, isVisible, onClose }) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isVisible) {
      // Small delay so the enter animation triggers
      requestAnimationFrame(() => setShow(true));

      if (duration > 0) {
        const timer = setTimeout(() => {
          setShow(false);
          setTimeout(() => onClose?.(), 300); // Wait for exit animation
        }, duration);
        return () => clearTimeout(timer);
      }
    } else {
      setShow(false);
    }
  }, [isVisible, duration, onClose]);

  const handleClose = useCallback(() => {
    setShow(false);
    setTimeout(() => onClose?.(), 300);
  }, [onClose]);

  if (!isVisible) return null;

  const icon =
    type === "success" ? (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" />
        <path d="M6.5 10l2.5 2.5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : type === "error" ? (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" />
        <path d="M7 7l6 6M13 7l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ) : (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" />
        <path d="M10 9v4M10 7h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );

  return (
    <div className={`mango-toast mango-toast--${type} ${show ? "mango-toast--visible" : ""}`}>
      <span className="mango-toast__icon">{icon}</span>
      <span className="mango-toast__message">{message}</span>
      <button className="mango-toast__close" onClick={handleClose} aria-label="Dismiss">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
};

export default Toast;
